"use client";

import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Clock, ShieldCheck, CircleCheck as CheckCircle2, MapPin, Phone, MessageCircle, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PHONE_LOCAL, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
const whyUsPoints = [{
  icon: ShieldCheck,
  title: "خبرة متخصصة بحي العقيق",
  description: "فهم دقيق لاحتياجات منازل ومطابخ حي العقيق بالرياض وتوفير حلول احترافية تلائم طبيعة الأفران الحديثة والتقليدية."
}, {
  icon: Clock,
  title: "استجابة فورية ومواعيد دقيقة",
  description: "سرعة في تلبية طلبات سكان الحي والالتزام بالحضور في الموعد المحدد لتقديم الخدمة المنزلية دون تأخير."
}, {
  icon: Sparkles,
  title: "فريق فني معتمد ومحترف",
  description: "فنيون مدربون على أعلى المعايير لفك وتركيب أجزاء الفرن بدقة، وإتمام أعمال الصيانة والتنظيف بأمان كامل."
}, {
  icon: CheckCircle2,
  title: "منظفات آمنة ومصرحة",
  description: "مواد كيميائية معتمدة تزيل أصعب الدهون والترسبات الكربونية بكفاءة دون الإضرار بطلاء الفرن أو الأجزاء الداخلية."
}];
const coverageDistricts = ["حي العقيق", "حي الصحافة", "حي حطين", "حي الغدير", "حي الملقا", "حي النخيل", "حي الياسمين", "عموم مناطق شمال وشمال غرب الرياض"];
const faqs = [{
  question: "كم مرة يُنصح بتنظيف وغسيل الفرن بشكل عميق؟",
  answer: "نوصي بتنظيف الفرن عميقاً كل 3 إلى 6 أشهر وفق كثافة الاستخدام المنزلي، للمحافظة على نظافة الهواء داخل المطبخ وكفاءة توزيع الحرارة وتجنب تراكم الدهون الكربونية الضارة."
}, {
  question: "ما هي المدة المتوقعة للزيارة المنزلية وتنظيف الفرن؟",
  answer: "تستغرق الزيارة عادة ما بين 45 دقيقة إلى ساعتين كحد أقصى، تبعاً لحجم الفرن ودرجة تراكم الدهون وعدد العيون والملحقات التي تتطلب فكاً وتلميعاً."
}, {
  question: "هل مواد التنظيف آمنة للأفران الغازية والكهربائية على حد سواء؟",
  answer: "نعم تماماً، نعتمد مواد متخصصة غير كاوية وصديقة للمعادن لا تتفاعل مع أسلاك الكهرباء ولا تضر بشعلات الغاز ومستشعرات الأمان، مع مراعاة حماية الطلاء والمينا الداخلية للفرن."
}, {
  question: "كيف يمكنني حجز موعد لخدمة تنظيف الأفران بحي العقيق؟",
  answer: `يمكنك الحجز مباشرة وفورياً من خلال النقر على زر الواتساب أو الاتصال على الرقم المباشر (${PHONE_LOCAL}). سيتواصل معك فريق خدمة العملاء لتحديد موعد الزيارة الأنسب لك.`
}];
export default function AlAqiqOvenCleaningService() {
  return <section id="alaqiqovencleaningservice" data-nav="light" dir="rtl" className="relative bg-background text-foreground py-16 sm:py-24 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Block 1: Service Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center mb-20 sm:mb-28">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45
        }} className="lg:col-span-7 flex flex-col justify-center">
            <div className="w-8 h-[3px] bg-primary mb-4" />
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-6 leading-tight">
              تنظيف وصيانة الأفران بحي العقيق بالرياض
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6">
              تقدم شركة <strong>لمسة احتراف</strong> خدمة متكاملة ومتخصصة في تنظيف وغسيل الأفران المنزلية داخل حي العقيق. تتضمن الخدمة التنظيف العميق لغرف الأفران، الشوايات، الصواني الداخلية، والأبواب الزجاجية المزدوجة مع تفكيكها وتلميعها بعناية فائقة.
            </p>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-8">
              نعتمد على مواد متطورة لإزالة أقسى ترسبات الدهون والزيوت المحترقة والتكلسات الكربونية بفاعلية مؤكدة، دون إحداث أي خدوش أو تلف في طلاء الفرن أو مفاتيح التشغيل أو الحساسات الداخلية الحساسة.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-6 shadow-sm" asChild>
                <Link to={WHATSAPP_HREF} newTab>
                  <MessageCircle className="size-4 ml-2" />
                  حجز موعد عبر واتساب
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-border text-foreground hover:bg-muted h-11 px-6" asChild>
                <a href={PHONE_TEL_HREF}>
                  <Phone className="size-4 ml-2" />
                  اتصال مباشر
                </a>
              </Button>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          delay: 0.1
        }} className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-border shadow-md bg-muted aspect-[4/3] sm:aspect-[5/4] lg:aspect-square flex items-center justify-center">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/alaqiq_oven_cleaning_restoration.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/alaqiq_oven_cleaning_restoration.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/alaqiq_oven_cleaning_restoration.webp-wvc-sizes" alt="فني تنظيف وصيانة أفران يزيل الدهون من حجرة الفرن بحي العقيق" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
          </motion.div>
        </div>

        {/* Block 2: Why Choose لمسة احتراف */}
        <div className="mb-20 sm:mb-28">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45
        }} className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <div className="w-8 h-[3px] bg-primary mx-auto mb-3" />
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground mb-4">
              لماذا تختار لمسة احتراف في حي العقيق؟
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              معايير احترافية تضمن لك فرناً نظيفاً وآمناً كالجديد، مع الحفاظ على أعلى مستويات السلامة والنظافة داخل منزلك.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyUsPoints.map((point, index) => {
            const Icon = point.icon;
            return <motion.div key={point.title} data-index={index} initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.08
            }}>
                  <Card className="h-full border-border bg-card shadow-xs hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="size-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-4">
                        <Icon className="size-6" />
                      </div>
                      <CardTitle className="text-lg font-semibold text-card-foreground">
                        {point.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {point.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Block 3: Coverage & Home Visits */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45
      }} className="mb-20 sm:mb-28">
          <div className="rounded-2xl border border-border bg-muted/60 p-6 sm:p-10 shadow-xs">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-5">
                <div className="inline-flex items-center gap-2 text-primary font-medium text-sm mb-3">
                  <MapPin className="size-4" />
                  <span>تغطية فورية وسريعة</span>
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">
                  نطاق الخدمة والزيارات المنزلية بحي العقيق والمناطق المحيطة
                </h3>
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                  نصل إليك عبر أسطول من سيارات الخدمة المجهزة بكافة الأدوات ومواد التنظيف المتخصصة، لتقديم خدمة منزلية فورية ومريحة لسكان حي العقيق وكافة أحياء شمال الرياض.
                </p>
              </div>

              <div className="lg:col-span-7">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {coverageDistricts.map((district, i) => <div key={district} data-index={i} className="bg-card border border-border rounded-lg p-3 text-center text-xs sm:text-sm font-medium text-foreground flex items-center justify-center shadow-2xs">
                      {district}
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Block 4: FAQ Accordion */}
        <div className="mb-20 sm:mb-28 max-w-3xl mx-auto">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45
        }} className="text-center mb-10">
            <div className="inline-flex items-center gap-2 text-primary text-sm font-semibold mb-2">
              <HelpCircle className="size-4" />
              <span>إجابات واضحة</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
              الأسئلة الشائعة حول تنظيف الأفران بحي العقيق
            </h2>
            <p className="text-sm text-muted-foreground">
              كل ما تود معرفته عن خطوات الحجز، المدة، والأمان أثناء الزيارة المنزلية.
            </p>
          </motion.div>

          <Accordion type="single" collapsible className="w-full bg-card border border-border rounded-xl p-2 sm:p-4 shadow-xs">
            {faqs.map((faq, index) => <AccordionItem key={faq.question} value={`item-${index}`} className="border-border">
                <AccordionTrigger className="text-right text-base font-medium hover:no-underline py-4 px-2">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed px-2 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {/* Block 5: Call-to-action Block */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45
      }} className="rounded-2xl bg-secondary text-secondary-foreground p-8 sm:p-12 text-center shadow-lg border border-secondary relative overflow-hidden">
          <div className="max-w-2xl mx-auto relative z-10">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              جاهزون لخدمتكم اليوم في حي العقيق بالرياض
            </h3>
            <p className="text-secondary-foreground/85 text-base sm:text-lg mb-8 leading-relaxed">
              تخلص من الدهون المتراكمة واستعد كفاءة ولمعان فرنك بكل سهولة. اتصل بنا الآن أو تواصل مباشرة عبر تطبيق واتساب لحجز زيارتك المنزلية.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 text-base font-semibold shadow-md" asChild>
                <Link to={WHATSAPP_HREF} newTab>
                  <MessageCircle className="size-5 ml-2" />
                  حجز عبر واتساب: {PHONE_LOCAL}
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-secondary-foreground/30 text-secondary-foreground hover:bg-secondary-foreground/10 h-12 px-8 text-base font-medium" asChild>
                <a href={PHONE_TEL_HREF}>
                  <Phone className="size-5 ml-2" />
                  اتصال: {PHONE_LOCAL}
                </a>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}